import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import { AuthGate } from "./auth/AuthGate.tsx";
import { AdminDashboard } from "./admin/AdminDashboard.tsx";
import { PromptAdminShell } from "./admin/PromptAdminShell.tsx";
import { PromptAdminPage } from "./admin/PromptAdminPage.tsx";
import { PromptDiagnosticsPage } from "./admin/PromptDiagnosticsPage.tsx";
import { PromptReplayPage } from "./admin/PromptReplayPage.tsx";
import { SessionManagementPage } from "./admin/SessionManagementPage.tsx";
import { AppToaster } from "./components/ui/sonner.tsx";
import { ModeratorView } from "./moderator/ModeratorView.tsx";
import { ParticipantView } from "./participant/ParticipantView.tsx";
import { ProjectorView } from "./projector/ProjectorView.tsx";
import { ThemeProvider } from "./lib/useTheme.tsx";
import { LiveSessionRedirect } from "./lib/LiveSessionRedirect.tsx";
import { SessionHandoffCoordinator } from "./lib/SessionHandoffCoordinator.tsx";
import {
  buildJoinPath,
  buildModeratorPath,
  buildProjectorPath,
  normalizeJoinCode,
} from "./lib/join.ts";
import { DEFAULT_PROMPT_WORKFLOW } from "../shared/promptContracts/registry.ts";
import "./styles/index.css";

const convex = new ConvexReactClient(
  import.meta.env.VITE_CONVEX_URL ?? "https://acoustic-setter-823.convex.cloud",
);

const LLM_SETTINGS_ROOT = "/admin/llm-settings";

function OperatorSessionRedirect() {
  const { joinCode } = useParams();
  const code = normalizeJoinCode(joinCode);
  return <Navigate to={code ? `/admin/session/${code}` : "/admin"} replace />;
}

function PromptRootRedirect() {
  return <Navigate to={`${LLM_SETTINGS_ROOT}/${DEFAULT_PROMPT_WORKFLOW}`} replace />;
}

function LegacyPromptRootRedirect() {
  const { search } = useLocation();
  return <Navigate to={`${LLM_SETTINGS_ROOT}${search}`} replace />;
}

function OperatorPromptRedirect() {
  const { promptSlug } = useParams();
  return <Navigate to={promptSlug ? `${LLM_SETTINGS_ROOT}/${promptSlug}` : LLM_SETTINGS_ROOT} replace />;
}

function OperatorPromptReplayRedirect() {
  const { promptSlug } = useParams();
  return <Navigate to={promptSlug ? `${LLM_SETTINGS_ROOT}/${promptSlug}/replay` : LLM_SETTINGS_ROOT} replace />;
}

function OperatorPromptDiagnosticsRedirect() {
  const { promptSlug } = useParams();
  const { search } = useLocation();
  return <Navigate to={buildDiagnosticsPath(promptSlug, search)} replace />;
}

function PromptScopedDiagnosticsRedirect() {
  const { promptSlug } = useParams();
  const { search } = useLocation();
  return <Navigate to={buildDiagnosticsPath(promptSlug, search)} replace />;
}

function LegacyPromptRedirect() {
  const { promptSlug } = useParams();
  return <Navigate to={promptSlug ? `${LLM_SETTINGS_ROOT}/${promptSlug}` : LLM_SETTINGS_ROOT} replace />;
}

function LegacyPromptReplayRedirect() {
  const { promptSlug } = useParams();
  return <Navigate to={promptSlug ? `${LLM_SETTINGS_ROOT}/${promptSlug}/replay` : LLM_SETTINGS_ROOT} replace />;
}

function LegacyPromptDiagnosticsRedirect() {
  const { search } = useLocation();
  return <Navigate to={`${LLM_SETTINGS_ROOT}/diagnostics${search}`} replace />;
}

function buildDiagnosticsPath(promptSlug: string | undefined, search: string) {
  const params = new URLSearchParams(search);
  if (promptSlug) params.set("workflow", promptSlug);
  const query = params.toString();
  return `${LLM_SETTINGS_ROOT}/diagnostics${query ? `?${query}` : ""}`;
}

function AppLayout() {
  const { pathname } = useLocation();

  return (
    <>
      <SessionHandoffCoordinator />
      <Outlet />
      <AppToaster routePath={pathname} />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <AppLayout />,
    children: [
      { path: "/", element: <AuthGate role="operator"><AdminDashboard /></AuthGate> },
      { path: "/admin", element: <AuthGate role="operator"><AdminDashboard /></AuthGate> },
      { path: "/admin/session/:joinCode/*", element: <AuthGate role="operator"><AdminDashboard /></AuthGate> },
      { path: "/admin/llm-settings", element: <AuthGate role="operator"><PromptRootRedirect /></AuthGate> },
      { path: "/admin/llm-settings/diagnostics", element: <AuthGate role="operator"><PromptDiagnosticsPage /></AuthGate> },
      {
        path: "/admin/llm-settings/:promptSlug",
        element: <AuthGate role="operator"><PromptAdminShell /></AuthGate>,
        children: [
          { index: true, element: <PromptAdminPage /> },
          { path: "replay", element: <PromptReplayPage /> },
          { path: "diagnostics", element: <PromptScopedDiagnosticsRedirect /> },
        ],
      },
      { path: "/admin/prompts", element: <AuthGate role="operator"><LegacyPromptRootRedirect /></AuthGate> },
      { path: "/admin/prompts/diagnostics", element: <AuthGate role="operator"><LegacyPromptDiagnosticsRedirect /></AuthGate> },
      { path: "/admin/prompts/:promptSlug/replay", element: <AuthGate role="operator"><LegacyPromptReplayRedirect /></AuthGate> },
      { path: "/admin/prompts/:promptSlug/diagnostics", element: <AuthGate role="operator"><PromptScopedDiagnosticsRedirect /></AuthGate> },
      { path: "/admin/prompts/:promptSlug", element: <AuthGate role="operator"><LegacyPromptRedirect /></AuthGate> },
      { path: "/admin/sessions", element: <AuthGate role="operator"><SessionManagementPage /></AuthGate> },
      { path: "/operator", element: <Navigate to="/admin" replace /> },
      { path: "/operator/session/:joinCode", element: <OperatorSessionRedirect /> },
      { path: "/operator/prompts", element: <Navigate to={LLM_SETTINGS_ROOT} replace /> },
      { path: "/operator/prompts/diagnostics", element: <OperatorPromptDiagnosticsRedirect /> },
      { path: "/operator/prompts/:promptSlug", element: <OperatorPromptRedirect /> },
      { path: "/operator/prompts/:promptSlug/replay", element: <OperatorPromptReplayRedirect /> },
      { path: "/operator/prompts/:promptSlug/diagnostics", element: <OperatorPromptDiagnosticsRedirect /> },
      { path: "/operator/sessions", element: <Navigate to="/admin/sessions" replace /> },
      {
        path: "/moderator",
        element: (
          <AuthGate role="moderator">
            <LiveSessionRedirect
              buildPath={buildModeratorPath}
              loading={<div className="moderator"><div className="loading">Loading session...</div></div>}
              noSession={<ModeratorView />}
            />
          </AuthGate>
        ),
      },
      { path: "/moderator/session/:joinCode", element: <AuthGate role="moderator"><ModeratorView /></AuthGate> },
      {
        path: "/join",
        element: (
          <LiveSessionRedirect
            buildPath={buildJoinPath}
            loading={<div className="participant-page"><div className="loading">Loading session...</div></div>}
            noSession={<Navigate to="/participate" replace />}
          />
        ),
      },
      { path: "/join/:joinCode/*", element: <ParticipantView /> },
      { path: "/participate", element: <ParticipantView /> },
      {
        path: "/projector",
        element: (
          <LiveSessionRedirect
            buildPath={buildProjectorPath}
            loading={<div className="projector-page"><div className="projector-empty">Loading…</div></div>}
            noSession={<ProjectorView />}
          />
        ),
      },
      { path: "/projector/session/:joinCode", element: <ProjectorView /> },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider>
      <ConvexProvider client={convex}>
        <RouterProvider router={router} />
      </ConvexProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
